import { Box, Button, Checkbox, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { dataProvider } from "api";
import React from "react";
import { useListContext } from "react-admin";
import { useNotifyError } from "utils/notifiers/useNotifyError";
import { useNotifySuccess } from "utils/notifiers/useNotifySuccess";

import { OrderStatus } from "../../../../../../entities/OrderStatus";
import { OrderStatusMovementAndReservationPallet } from "../../../../../../entities/OrderStatus";
import { BoxMovementsGrid } from "../../../components/BoxMovementsGrid";
import { EmptyPalletConfirmDialog } from "./EmptyPalletConfirmDialog";

const useStyles = makeStyles((theme) => ({
	actionsWrapper: {
		width: "100%",
		display: "flex",
		justifyContent: "center",
		marginBottom: theme.spacing(2),
	},

	actionsInner: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		transform: "translateX(-24px)",
	},

	confirmMode: {
		"&.MuiButton-contained": {
			backgroundColor: "#f44336",
			color: "white",
			"&:hover": {
				backgroundColor: "#d32f2f",
			},
		},
	},
}));

const getEmptySourcePalletSsccs = (palletBoxMovements, boxMovements) => {
	const movementsById = {};
	palletBoxMovements.forEach((m) => {
		movementsById[m.id] = m;
	});

	// Calculate resulting quantity per source pallet after applying changes
	const sourcePallets = {};

	boxMovements.forEach(({ id, is_confirmed }) => {
		const movement = movementsById[id];
		if (!movement) return;

		const wasConfirmed = !!movement.completed_at;
		if (is_confirmed === wasConfirmed) return;

		const palletId = movement.source_pallet_id;
		if (!sourcePallets[palletId]) {
			sourcePallets[palletId] = {
				sscc: movement.source_pallet_sscc,
				resultingPalletQty: movement.source_pallet_quantity,
			};
		}
		sourcePallets[palletId].resultingPalletQty += is_confirmed
			? -movement.quantity
			: movement.quantity;
	});

	return Object.values(sourcePallets)
		.filter(({ resultingPalletQty }) => resultingPalletQty === 0)
		.map(({ sscc }) => sscc);
};

export const BoxMovementsDatagrid = ({
	translate,
	pallet,
	orderId,
	terminalId,
	storagePlaces,
	isConfirmBoxMovementMode,
	setIsConfirmBoxMovementMode,
	orderStatus,
	fetchReservationTotals,
}) => {
	const notifySuccess = useNotifySuccess();
	const notifyError = useNotifyError();
	const { refetch } = useListContext();
	const classes = useStyles();
	const [selectedBoxMovements, setSelectedBoxMovements] = React.useState([]);
	const [emptyPalletDialogData, setEmptyPalletDialogData] = React.useState(null);
	const isBoxMovementAllowed = OrderStatusMovementAndReservationPallet.includes(orderStatus);

	const handleDeleteMovement = async (boxMovementId) => {
		try {
			await dataProvider.create(`box-movements-revert`, {
				data: {
					order_id: orderId,
					box_movement_id: boxMovementId,
				},
			});
			notifySuccess(translate("tab.picking_list.box_movement.notifications.movement_deleted"));
			refetch();
		} catch (error) {
			notifyError(
				error.message ||
					translate("tab.picking_list.box_movement.notifications.failed_to_delete_movement")
			);
		}
	};

	const handleDeleteAllForPallet = async () => {
		try {
			await dataProvider.create(`box-movements-revert`, {
				data: {
					order_id: orderId,
					pallet_id: pallet.id,
				},
			});
			notifySuccess(translate("tab.picking_list.box_movement.notifications.all_movements_deleted"));
			refetch();
		} catch (error) {
			notifyError(
				error.message ||
					translate("tab.picking_list.box_movement.notifications.failed_to_delete_all_movements")
			);
		}
	};

	const buildBoxMovementsPayload = () => {
		const boxMovements = [];

		selectedBoxMovements.forEach((movementId) => {
			boxMovements.push({
				id: movementId,
				is_confirmed: true,
			});
		});
		pallet.box_movements.forEach((movement) => {
			if (movement.completed_at && !selectedBoxMovements.includes(movement.id)) {
				boxMovements.push({
					id: movement.id,
					is_confirmed: false,
				});
			}
		});

		return boxMovements;
	};

	const submitConfirmation = async (boxMovements) => {
		try {
			await dataProvider.updateManyPartial("box-movements", {
				data: { box_movements: boxMovements, order_id: orderId },
			});

			notifySuccess(translate("tab.picking_list.box_movement.notifications.movements_confirmed"));
			refetch();
			fetchReservationTotals && fetchReservationTotals();
			setIsConfirmBoxMovementMode(false);
			setSelectedBoxMovements([]);
		} catch (error) {
			notifyError(error);
		}
	};

	const handleConfirmSelectedMovements = async () => {
		const boxMovements = buildBoxMovementsPayload();

		if (boxMovements.length === 0) {
			notifyError(translate("tab.picking_list.box_movement.notifications.no_changes"), {
				type: "info",
			});
			return;
		}

		const emptySsccs = getEmptySourcePalletSsccs(pallet.box_movements, boxMovements);

		if (emptySsccs.length > 0) {
			setEmptyPalletDialogData({ boxMovements, ssccs: emptySsccs });
			return;
		}

		await submitConfirmation(boxMovements);
	};

	const handleEmptyPalletDialogYes = async () => {
		const { boxMovements } = emptyPalletDialogData;
		setEmptyPalletDialogData(null);
		await submitConfirmation(boxMovements);
	};

	const handleEmptyPalletDialogNo = async () => {
		const { boxMovements } = emptyPalletDialogData;
		setEmptyPalletDialogData(null);
		notifyError("tab.picking_list.box_movement.notifications.contact_manager_deviation", {
			type: "warning",
		});
		await submitConfirmation(boxMovements);
	};

	const hasUncompletedMovements = pallet?.box_movements?.some((m) => !m.completed_at);

	const handleToggleConfirmMode = () => {
		setIsConfirmBoxMovementMode((prev) => {
			const next = !prev;

			if (!prev && next) {
				setSelectedBoxMovements(
					pallet.box_movements.filter((m) => m.completed_at).map((m) => m.id)
				);
			}

			if (prev && !next) {
				setSelectedBoxMovements([]);
			}

			return next;
		});
	};

	const handleSelectBoxMovement = (id) => {
		setSelectedBoxMovements((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
		);
	};

	const handleToggleAll = () => {
		if (selectedBoxMovements.length > 0) {
			setSelectedBoxMovements([]);
		} else {
			const allMovementIds = pallet.box_movements.map((m) => m.id);
			setSelectedBoxMovements(allMovementIds);
		}
	};

	return (
		<Box>
			<Box className={classes.actionsWrapper}>
				<Box className={classes.actionsInner}>
					{orderStatus === OrderStatus.PLUKK_STARTED && (
						<>
							{isConfirmBoxMovementMode && (
								<Box display="flex" alignItems="center">
									<Typography variant="body2" color="textSecondary">
										{translate("osfc.buttons.select_all")}
									</Typography>
									<Checkbox
										checked={selectedBoxMovements.length > 0}
										onChange={handleToggleAll}
										size="small"
										color="primary"
									/>
								</Box>
							)}
							<Button
								variant="contained"
								color={isConfirmBoxMovementMode ? undefined : "primary"}
								className={isConfirmBoxMovementMode ? classes.confirmMode : ""}
								size="small"
								onClick={handleToggleConfirmMode}
							>
								{isConfirmBoxMovementMode
									? translate("osfc.buttons.cancel")
									: translate("osfc.buttons.confirm")}
							</Button>
							{isConfirmBoxMovementMode && (
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={handleConfirmSelectedMovements}
								>
									{translate("osfc.buttons.apply")}
								</Button>
							)}
						</>
					)}
					{hasUncompletedMovements && !isConfirmBoxMovementMode && isBoxMovementAllowed && (
						<Button
							variant="outlined"
							color="secondary"
							size="small"
							startIcon={<DeleteIcon />}
							onClick={handleDeleteAllForPallet}
						>
							{translate("tab.picking_list.box_movement.buttons.delete_all_movements_for_pallet")}
						</Button>
					)}
				</Box>
			</Box>

			<BoxMovementsGrid
				translate={translate}
				pallet={pallet}
				terminalId={terminalId}
				storagePlaces={storagePlaces}
				showActions
				isActionsDisabled={!isBoxMovementAllowed}
				isConfirmMode={isConfirmBoxMovementMode}
				onDeleteMovement={handleDeleteMovement}
				selectedBoxMovements={selectedBoxMovements}
				onSelectBoxMovement={handleSelectBoxMovement}
			/>

			<EmptyPalletConfirmDialog
				open={!!emptyPalletDialogData}
				ssccs={emptyPalletDialogData?.ssccs || []}
				translate={translate}
				onConfirm={handleEmptyPalletDialogYes}
				onReject={handleEmptyPalletDialogNo}
				onClose={() => setEmptyPalletDialogData(null)}
			/>
		</Box>
	);
};
